/**
 * Public landing pages for the ten curated primary tools.
 *
 * Each entry powers one indexable page at /ai-tools/<slug> with its own title,
 * description, steps and FAQ, and a button that links straight into the live
 * tool. Copy is hand-written per tool — do not generate it from the registry.
 */

export interface ToolLanding {
  /** Matches the tool id in TOOL_REGISTRY / PRIMARY_TOOL_IDS. */
  id: string;
  /** URL slug for /ai-tools/<slug>. */
  slug: string;
  name: string;
  /** <title> for the page. */
  seoTitle: string;
  /** Meta description. */
  seoDescription: string;
  headline: string;
  subheadline: string;
  /** Where the "Open" button goes — the live tool. */
  launchTo: string;
  launchLabel: string;
  /** Sign-in required to run it. */
  requiresAuth: boolean;
  category: string;
  whoFor: string[];
  steps: string[];
  faqs: Array<{ q: string; a: string }>;
  related: Array<{ label: string; to: string }>;
}

const free = (name: string) => ({
  q: `Is ${name} free?`,
  a: `Yes. ${name} is part of GenieBot and free to use — there is no paywall and no credits to buy.`,
});

export const TOOL_LANDINGS: ToolLanding[] = [
  {
    id: "chat",
    slug: "ai-chat-assistant",
    name: "GenieBot Chat",
    seoTitle: "Free AI Chat Assistant — Ask, Draft and Plan | GenieBot",
    seoDescription:
      "A free AI chat assistant for drafting, summarising, planning and thinking things through, with your conversations saved to your own account.",
    headline: "An AI chat assistant that keeps your work",
    subheadline:
      "Ask questions, draft messages, summarise long documents and plan your week — then send any answer straight into another GenieBot tool.",
    launchTo: "/chat",
    launchLabel: "Open GenieBot Chat",
    requiresAuth: true,
    category: "AI",
    whoFor: [
      "People who want one place to write, summarise and plan",
      "Anyone tired of losing good answers in a chat history they can't search",
      "Teams who want to hand a result over to another tool without copy-paste",
    ],
    steps: [
      "Sign in with your free GenieBot account.",
      "Type what you need — a draft, a summary, a plan, a decision to think through.",
      "Send the answer to another tool, or export it as PDF, Markdown or plain text.",
    ],
    faqs: [
      free("GenieBot Chat"),
      {
        q: "Are my conversations private?",
        a: "Your chats are stored against your own account and are not visible to other users. You can export or delete your data at any time from your data controls.",
      },
      {
        q: "Can I carry an answer into another tool?",
        a: "Yes. Every result has a “Send to” option that carries the text into tools like Blog Assistant or the Prompt Template Factory.",
      },
    ],
    related: [
      { label: "Blog Assistant", to: "/ai-tools/ai-blog-post-writer" },
      { label: "AI Image Generator", to: "/ai-tools/ai-image-generator" },
    ],
  },
  {
    id: "contract-risk-analyzer",
    slug: "contract-risk-analyzer",
    name: "Contract Risk Analyzer",
    seoTitle: "Free Contract Risk Analyzer — Spot Risky Clauses | GenieBot",
    seoDescription:
      "Upload a contract and get a plain-English risk read: flagged clauses, what each one means for you, and what to ask for before signing.",
    headline: "Read a contract before it reads you",
    subheadline:
      "Get a clause-by-clause risk view of any agreement in plain English, with the parts worth pushing back on called out.",
    launchTo: "/contract-analyzer",
    launchLabel: "Analyze a contract",
    requiresAuth: true,
    category: "Security",
    whoFor: [
      "Freelancers and small businesses signing client agreements",
      "Anyone reviewing an offer, lease or vendor contract without a lawyer on call",
      "Teams who want a first pass before paying for legal review",
    ],
    steps: [
      "Sign in and upload or paste your contract.",
      "Review the risk score and the flagged clauses, each explained in plain English.",
      "Export the summary or share it with whoever needs to decide.",
    ],
    faqs: [
      free("Contract Risk Analyzer"),
      {
        q: "Is this legal advice?",
        a: "No. It is an informational first pass to help you ask better questions. For anything binding or high value, have a qualified lawyer review it.",
      },
      {
        q: "What happens to the contract I upload?",
        a: "Documents are encrypted and stored against your account only, with access logged. You can delete them whenever you want.",
      },
    ],
    related: [
      { label: "Cyber Security Assistant", to: "/ai-tools/cyber-security-assistant" },
      { label: "Security checklist", to: "/security-checklist" },
    ],
  },
  {
    id: "ai-image-generator",
    slug: "ai-image-generator",
    name: "AI Image Generator",
    seoTitle: "Free AI Image Generator — Text to Image | GenieBot",
    seoDescription:
      "Turn a written description into an image in seconds. Free AI image generation with no sign-up required to try it.",
    headline: "Describe it, and get the picture",
    subheadline:
      "Type what you want to see and get an image back in seconds — for posts, mock-ups, thumbnails and slides.",
    launchTo: "/ai-image-generator",
    launchLabel: "Generate an image",
    requiresAuth: false,
    category: "AI",
    whoFor: [
      "Creators who need a visual now, not a stock-photo subscription",
      "Small businesses making social posts and banners",
      "Anyone sketching an idea before commissioning the real thing",
    ],
    steps: [
      "Write a short description of the image you want.",
      "Generate, then adjust the wording until it looks right.",
      "Download the result and use it wherever you need it.",
    ],
    faqs: [
      free("AI Image Generator"),
      {
        q: "Do I need an account?",
        a: "No. You can generate images without signing in. An account only helps if you want your results kept.",
      },
      {
        q: "Can I use the images commercially?",
        a: "Generated images are yours to use, but check that your final use does not rely on a recognisable brand, logo or real person.",
      },
    ],
    related: [
      { label: "AI Video Generator", to: "/ai-video-generator" },
      { label: "Creative Director", to: "/tools/creative-director" },
    ],
  },
  {
    id: "geniebot-rezzy",
    slug: "ai-resume-enhancer",
    name: "AI Resume Enhancer",
    seoTitle: "Free AI Resume Enhancer — Rewrite Your CV | GenieBot",
    seoDescription:
      "Paste your resume and get sharper, results-focused bullet points tailored to the job you actually want. Free to use.",
    headline: "A resume that says what you actually did",
    subheadline:
      "Turn vague duties into specific, results-led bullet points, matched to the role you are applying for.",
    launchTo: "/tools?tool=geniebot-rezzy",
    launchLabel: "Improve my resume",
    requiresAuth: true,
    category: "Career",
    whoFor: [
      "Job seekers rewriting a resume for a specific role",
      "Career changers translating past work into new language",
      "Anyone whose resume reads like a job description",
    ],
    steps: [
      "Paste your current resume text.",
      "Add the role or job description you are targeting.",
      "Review the rewritten bullets and export the version you want.",
    ],
    faqs: [
      free("AI Resume Enhancer"),
      {
        q: "Will it invent experience I don't have?",
        a: "It rewrites what you give it. Always read the result and correct anything that overstates what you did.",
      },
      {
        q: "Can I export it?",
        a: "Yes — PDF, Markdown or plain text, so you can drop it into your own template.",
      },
    ],
    related: [
      { label: "GenieBot Chat", to: "/ai-tools/ai-chat-assistant" },
      { label: "All tools", to: "/tools" },
    ],
  },
  {
    id: "pdf-storybook-architect",
    slug: "pdf-storybook-architect",
    name: "PDF StoryBook Architect",
    seoTitle: "PDF StoryBook Architect — Write a Story, Export a Book | GenieBot",
    seoDescription:
      "Generate an original short-fiction manuscript with chapters and page breaks, ready to export as a PDF. Free in GenieBot.",
    headline: "From an idea to a finished manuscript",
    subheadline:
      "Build an original short story with structure, chapter breaks and clean page formatting you can export straight to PDF.",
    launchTo: "/pdf-storybook-architect",
    launchLabel: "Start a storybook",
    requiresAuth: true,
    category: "Creative",
    whoFor: [
      "Writers who want a full draft to edit rather than a blank page",
      "Parents and teachers making a short illustrated story",
      "Anyone producing a printable booklet quickly",
    ],
    steps: [
      "Describe the story: premise, tone, length and audience.",
      "Generate the manuscript and read it through, chapter by chapter.",
      "Export to PDF or Markdown with page breaks already in place.",
    ],
    faqs: [
      free("PDF StoryBook Architect"),
      {
        q: "Is the writing original?",
        a: "Yes — it generates new text from your premise. It will not reproduce an existing book.",
      },
      {
        q: "How long can a story be?",
        a: "Long enough for a short book. Very long manuscripts are best built a few chapters at a time.",
      },
    ],
    related: [
      { label: "Blog Assistant", to: "/ai-tools/ai-blog-post-writer" },
      { label: "GenieBot Tutor", to: "/ai-tools/how-to-guide-maker" },
    ],
  },
  {
    id: "cyber-security",
    slug: "cyber-security-assistant",
    name: "Cyber Security Assistant",
    seoTitle: "Free Cyber Security Assistant — Explain a Suspicious Command | GenieBot",
    seoDescription:
      "Paste a suspicious command line, script or log entry and get a plain-English explanation of what it does and whether it looks dangerous.",
    headline: "Find out what that command actually does",
    subheadline:
      "Paste a command, script or log line and get a clear explanation of its behaviour, plus the parts that should worry you.",
    launchTo: "/tools?tool=cyber-security",
    launchLabel: "Analyze a command",
    requiresAuth: true,
    category: "Security",
    whoFor: [
      "IT and support staff triaging something odd on a machine",
      "Developers reviewing a script before running it",
      "Anyone who was sent a command and told to “just run this”",
    ],
    steps: [
      "Paste the command, script or log entry.",
      "Read the breakdown: what it does, step by step, and what stands out.",
      "Decide, escalate, or export the explanation for your ticket.",
    ],
    faqs: [
      free("Cyber Security Assistant"),
      {
        q: "Does it replace a security team?",
        a: "No. It explains and flags. Anything that looks like a live incident should go to your security team.",
      },
      {
        q: "Should I paste secrets into it?",
        a: "No. Redact keys, tokens and passwords before pasting anything.",
      },
    ],
    related: [
      { label: "Security checklist", to: "/security-checklist" },
      { label: "Security guide", to: "/security-guide" },
    ],
  },
  {
    id: "blog-assistant",
    slug: "ai-blog-post-writer",
    name: "Blog Assistant",
    seoTitle: "Free AI Blog Post Writer — Outline and Draft | GenieBot",
    seoDescription:
      "Give a topic and get a structured blog post: outline, headings and a full draft you can edit and publish. Free in GenieBot.",
    headline: "A blog draft, not a blank page",
    subheadline:
      "Turn a topic into a structured post with headings and a full first draft you can edit in your own voice.",
    launchTo: "/tools?tool=blog-assistant",
    launchLabel: "Draft a post",
    requiresAuth: true,
    category: "Creative",
    whoFor: [
      "Solo founders keeping a blog alive alongside the actual work",
      "Marketers who need a first draft to shape",
      "Anyone who writes well but hates starting",
    ],
    steps: [
      "Enter your topic and who the post is for.",
      "Review the outline, then generate the full draft.",
      "Edit it, then export to Markdown or HTML for your site.",
    ],
    faqs: [
      free("Blog Assistant"),
      {
        q: "Will the draft be publishable as-is?",
        a: "Treat it as a strong first draft. Add your own examples and check every fact before publishing.",
      },
      {
        q: "Can I bring in a result from another tool?",
        a: "Yes. Use “Send to” from any GenieBot result and the text arrives here ready to work with.",
      },
    ],
    related: [
      { label: "GenieBot Chat", to: "/ai-tools/ai-chat-assistant" },
      { label: "Read the blog", to: "/blogs" },
    ],
  },
  {
    id: "travel-agent",
    slug: "ai-travel-itinerary-planner",
    name: "TravelGPT",
    seoTitle: "Free AI Travel Itinerary Planner — Day by Day | GenieBot",
    seoDescription:
      "Plan a trip with real dates and a real budget: day-by-day itinerary, travel times, weather context and sources you can check.",
    headline: "A trip plan with the details filled in",
    subheadline:
      "Dates, budget, pace and interests in — a day-by-day itinerary with travel times, weather context and checkable sources out.",
    launchTo: "/tools?tool=travel-agent",
    launchLabel: "Plan a trip",
    requiresAuth: false,
    category: "Lifestyle",
    whoFor: [
      "Anyone planning a trip and drowning in twenty browser tabs",
      "Travellers with a fixed budget who want it respected",
      "People who want a printable plan, not a list of links",
    ],
    steps: [
      "Enter your destination, dates, budget and what you enjoy.",
      "Get a day-by-day plan with a map, timings and cost estimates.",
      "Export the itinerary as a PDF or calendar file.",
    ],
    faqs: [
      free("TravelGPT"),
      {
        q: "Does it book anything?",
        a: "No. It plans. Bookings, prices and opening hours should always be confirmed with the provider before you commit.",
      },
      {
        q: "Do I need an account?",
        a: "No. You can plan a trip without signing in.",
      },
    ],
    related: [
      { label: "GenieBot Chat", to: "/ai-tools/ai-chat-assistant" },
      { label: "All tools", to: "/tools" },
    ],
  },
  {
    id: "geniebot-tutor",
    slug: "how-to-guide-maker",
    name: "GenieBot Tutor",
    seoTitle: "Free How-To Guide Maker — Step-by-Step Docs | GenieBot",
    seoDescription:
      "Turn a process you know into a clean, step-by-step how-to guide you can share or hand to a new starter. Free in GenieBot.",
    headline: "Write the guide once, hand it over forever",
    subheadline:
      "Describe a process and get a properly structured how-to guide with numbered steps, prerequisites and common mistakes.",
    launchTo: "/geniebot-tutor",
    launchLabel: "Create a guide",
    requiresAuth: true,
    category: "Education",
    whoFor: [
      "Anyone onboarding a new team member",
      "Support teams turning repeat questions into documentation",
      "Teachers and trainers writing clear instructions",
    ],
    steps: [
      "Describe the process and who will be following it.",
      "Review the generated steps and tighten anything that's off.",
      "Export as PDF or Markdown and share it.",
    ],
    faqs: [
      free("GenieBot Tutor"),
      {
        q: "Can it document my own internal process?",
        a: "Yes — the more detail you give about your steps and tools, the more accurate the guide will be.",
      },
      {
        q: "What formats can I export?",
        a: "PDF, Markdown, HTML and plain text.",
      },
    ],
    related: [
      { label: "PDF StoryBook Architect", to: "/ai-tools/pdf-storybook-architect" },
      { label: "Blog Assistant", to: "/ai-tools/ai-blog-post-writer" },
    ],
  },
];

/**
 * Tools whose landing page already exists at a dedicated URL. The tenth
 * curated tool, AI Video Generator, has its own page at /ai-video-generator —
 * pointing at it avoids publishing two near-identical pages.
 */
export const DEDICATED_TOOL_LANDINGS: Record<string, string> = {
  "ai-video-generator": "/ai-video-generator",
};

export const getToolLanding = (slug?: string) =>
  TOOL_LANDINGS.find((t) => t.slug === slug);

/** Landing page path for a curated tool id, if one exists. */
export const landingPathForTool = (toolId: string): string | null => {
  if (DEDICATED_TOOL_LANDINGS[toolId]) return DEDICATED_TOOL_LANDINGS[toolId];
  const entry = TOOL_LANDINGS.find((t) => t.id === toolId);
  return entry ? `/ai-tools/${entry.slug}` : null;
};

/** Every public landing URL for the curated tools, for the sitemap. */
export const ALL_TOOL_LANDING_PATHS = [
  ...TOOL_LANDINGS.map((t) => `/ai-tools/${t.slug}`),
  ...Object.values(DEDICATED_TOOL_LANDINGS),
];
